import type { NotNil } from "@infra-blocks/types";
import { isEqual } from "es-toolkit";
import { z } from "zod";
import { type Nilable, nilable } from "./schemas/nilable.js";
import { type Parser, parser } from "./schemas/parser.js";
import { trusted } from "./utils.js";

/**
The input schema type of properties.

It doesn't allow schemas to return `undefined` or `null` values. Whether
those can effectively be set in the final property is determined entirely
by the absence/presence of a default value.

When no default is provided, the effective schema associated with the property
(the one used in the setter) allows both null and undefined values as valid.

This is to remain coherent with 2 scenarios:
- The property is read before being set. In which case, it would return
`undefined`. Without a default value, we assume the getter can return
undefined, and therefore the setter should accept undefined.
- The property is associated with an attribute, and the attribute is removed
(null is forwarded to the setter). In the absence of default value, null
should be accepted as a valid value for the property.

When a default is provided, the getter and the schema are modified such that:
- The default value is returned if the underlying field has not been set yet.
- The default is used when the corresponding attribute, if any, has been removed.
*/
export type PropertyInputSchema = z.ZodType<NotNil<unknown>>;

export type AttributeProperty<
  A extends string,
  S extends z.ZodType,
> = Parser<S> & {
  attribute: A;
  reflect: true;
};

/**
Attribute properties are Lit reactive properties that map to an attribute.

Because attributes can be absent or removed from tags, the absent state
should be represented for the property as well. `null` is typically what the
web APIs use to describe a missing attribute. Lit also converts `undefined`
to an absent attribute when reflecting back.

For standardization purposes, this function returns an attribute property
schema that is always nullable and undefined, and should be the basis
to construct any attribute property schema.

Since those properties are serialized/deserialized to an from attributes,
they should only be simple, scalar types, such as strings, numbers and booleans.
*/
export function attributeProperty<
  A extends string,
  S extends PropertyInputSchema,
>(attribute: A, schema: S): AttributeProperty<A, Nilable<S>>;
export function attributeProperty<
  A extends string,
  S extends PropertyInputSchema,
>(
  attribute: A,
  schema: S,
  options: { defaultValue: z.output<S> },
): AttributeProperty<A, z.ZodType<z.output<S>, z.input<S>>>;
export function attributeProperty<
  A extends string,
  S extends PropertyInputSchema,
>(
  attribute: A,
  schema: S,
  options?: {
    defaultValue: z.output<S>;
  },
) {
  return Object.freeze({
    ...parser(normalize(schema, trusted(options?.defaultValue))),
    attribute,
    reflect: true,
  });
}

export type StateProperty<S extends z.ZodType> = Parser<S> & {
  attribute: false;
};

/**
State properties are properties that are not linked to any attribute.

They typically represent a complex type, such as an array of an object, but
they do not have to.

By default, the `hasChanged` property is remapped to es-toolkit's `isEqual`
function to support complex types.

Properties without a default value are made nilable. This is because a
call to the getter can produce `undefined` if it happens before the
value was explicitly set. In which case, instead of relying on the user
to set the default in the constructor (which can be forgotten), and to
stay conform with `attributeProperty` that has additional requirements,
the default behavior is to correctly express the valid states the property
can be in.

A default *can* be provided, in which case the aforementioned transformation
does not occur. In that case, the *getter returns the default value when
nil is found*. The default value is also used in the case where `null` is the
input, this is to remain coherent with `attributeProperty`'s behavior.'

So, there is no point in the code where calling the getter
might return undefined, or null, allowing the underlying property to be correctly
typed with a non nilable version of the schema.
*/
export function stateProperty<S extends PropertyInputSchema>(
  schema: S,
): StateProperty<Nilable<S>>;
export function stateProperty<S extends PropertyInputSchema>(
  schema: S,
  options: { defaultValue: z.output<S> },
): StateProperty<z.ZodType<z.output<S>, z.input<S>>>;
export function stateProperty<S extends PropertyInputSchema>(
  schema: S,
  options?: {
    defaultValue: NotNil<z.output<S>>;
  },
):
  | StateProperty<Nilable<S>>
  | StateProperty<z.ZodType<z.output<S>, z.input<S>>> {
  return Object.freeze({
    ...parser(normalize(schema, trusted(options?.defaultValue))),
    attribute: false,
    hasChanged: (current: z.output<S>, old: z.output<S>) =>
      !isEqual(current, old),
  });
}

function normalize<S extends PropertyInputSchema>(schema: S): Nilable<S>;
function normalize<S extends PropertyInputSchema>(
  schema: S,
  defaultValue: z.output<S>,
): z.ZodType<z.output<S>, z.input<S>>;
function normalize<S extends PropertyInputSchema>(
  schema: S,
  defaultValue?: z.output<S>,
) {
  if (defaultValue != null) {
    // If the value is null or undefined, undefined is forwarded to the schema,
    // such that both provide the same default value (default only works on
    // undefined).
    // This logic could technically also be provided in an attribute converter,
    // and might be in a future version of this implementation.
    return z.preprocess(
      (v) => v ?? undefined,
      schema.default(trusted(defaultValue)),
    );
  }
  return nilable(schema);
}
